import { AnimatePresence, motion } from 'framer-motion'
import { Trophy } from 'lucide-react'
import { FaWhatsapp } from 'react-icons/fa'
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { AppPhoneTrio } from '@/components/AppPhoneTrio'
import { HeroSportsVideo } from '@/components/HeroSportsVideo'
import { LandingUploadGate } from '@/components/landing/GameUploadCard'
import { isLandingLite } from '@/lib/landingLite'

const WHATSAPP_URL =
  'https://wa.me/96170510509?text=Hello!%20I%20would%20like%20to%20know%20more%20about%20Statbox.'

// CountUp — animates a number from 0 to `end` once when it enters viewport.
// Uses requestAnimationFrame + IntersectionObserver, no external deps.
const CountUp = ({
  end,
  duration = 1500,
  suffix = '+',
  className,
}: {
  end: number
  duration?: number
  suffix?: string
  className?: string
}) => {
  const [value, setValue] = useState(0)
  const [done, setDone] = useState(false)
  const elementRef = useRef<HTMLSpanElement>(null)
  const startedRef = useRef(false)

  useEffect(() => {
    const el = elementRef.current
    if (!el) return

    const startAnimation = () => {
      if (startedRef.current) return
      startedRef.current = true
      const start = performance.now()
      const tick = (now: number) => {
        const elapsed = now - start
        const t = Math.min(elapsed / duration, 1)
        const eased = 1 - Math.pow(1 - t, 3)
        setValue(Math.round(end * eased))
        if (t < 1) {
          requestAnimationFrame(tick)
        } else {
          setDone(true)
        }
      }
      requestAnimationFrame(tick)
    }

    if (typeof IntersectionObserver === 'undefined') {
      startAnimation()
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            startAnimation()
            observer.disconnect()
            break
          }
        }
      },
      { threshold: 0.1 }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [end, duration])

  return (
    <span ref={elementRef} className={className} aria-label={`${end.toLocaleString('en-US')}${suffix}`}>
      {value.toLocaleString('en-US')}
      {done ? suffix : ''}
    </span>
  )
}

const ROTATING_WORDS = ['stats', 'highlights', 'replays'] as const

// RotatingWord — slot-machine style vertical rotator. The container reserves
// space for the widest word so the surrounding copy never reflows on swap.
const RotatingWord = ({
  words = ROTATING_WORDS as readonly string[],
  intervalMs = 2200,
}: {
  words?: readonly string[]
  intervalMs?: number
}) => {
  const [index, setIndex] = useState(0)

  useEffect(() => {
    if (words.length <= 1) return
    const id = window.setInterval(() => {
      setIndex((i) => (i + 1) % words.length)
    }, intervalMs)
    return () => window.clearInterval(id)
  }, [words, intervalMs])

  const widest = words.reduce((a, b) => (a.length >= b.length ? a : b), '')

  return (
    <>
      <span
        aria-hidden="true"
        className="relative inline-block overflow-hidden font-bold text-[#71cdd9]"
        style={{ verticalAlign: 'bottom' }}
      >
        {/* Layout reservation — invisible spacer sized to the widest word */}
        <span className="invisible whitespace-nowrap">{widest}</span>
        <AnimatePresence initial={false}>
          <motion.span
            key={words[index]}
            initial={{ y: '100%', opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: '-100%', opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="absolute left-0 top-0 whitespace-nowrap"
          >
            {words[index]}
          </motion.span>
        </AnimatePresence>
      </span>
      <span
        className="sr-only"
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        {words.join(', ')}
      </span>
    </>
  )
}

const StickyWhatsApp = () => {
  return (
    <a
      href={WHATSAPP_URL}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Chat with us on WhatsApp"
      className="fixed z-50 bottom-[max(1.5rem,env(safe-area-inset-bottom))] right-[max(1.5rem,env(safe-area-inset-right))] inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-[0_18px_50px_-8px_rgba(0,0,0,0.6)] transition-transform duration-200 hover:bg-[#20BD5A] hover:scale-105"
    >
      <FaWhatsapp className="h-7 w-7" aria-hidden />
    </a>
  )
}

export const Hero = () => {
  const lite = isLandingLite()

  return (
    <>
      <section
        id="home"
        className="relative overflow-hidden bg-[#07080c] min-h-[100svh] pointer-events-none"
      >
        <h1 className="sr-only">Statbox: AI Stats and Highlights for Football, Basketball, and Padel</h1>
        <HeroSportsVideo />

        <div className="relative z-10 mx-auto flex min-h-[100svh] w-full max-w-[1280px] flex-col justify-center px-4 pt-28 pb-[calc(2.5rem+env(safe-area-inset-bottom))] sm:px-6 lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(280px,520px)] lg:items-center lg:gap-12 lg:px-10 lg:pt-24 lg:pb-20">
          <div className="flex flex-col items-center text-center pointer-events-auto lg:items-start lg:text-left">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/[0.05] backdrop-blur-sm pl-3 pr-3.5 py-1.5">
              <Trophy className="w-3.5 h-3.5 text-[#71cdd9]" aria-hidden />
              <span
                className="text-[13px] text-white/70 leading-none"
                style={{ fontFamily: 'Roboto, system-ui, sans-serif' }}
              >
                <CountUp
                  end={10000}
                  className="text-[15px] font-bold text-white tabular-nums"
                />
                <span> players on Statbox</span>
              </span>
            </div>

            <p className="mt-8 mb-5 text-[2.6rem] min-[400px]:text-[3rem] sm:text-[3.4rem] lg:text-[4.15rem] leading-[1.04] font-extrabold tracking-tight text-white text-balance">
              Fill every game.
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-white to-[#71cdd9]">
                Keep every player.
              </span>
            </p>

            <p className="text-base sm:text-lg text-white/62 leading-relaxed max-w-[420px] lg:max-w-xl">
              The only sports operating system that gives you{' '}
              <RotatingWord />
            </p>

            <nav
              className="mt-5 inline-flex items-center gap-2.5 text-[10.5px] sm:text-[11px] font-semibold uppercase text-white/70"
              style={{ fontFamily: '"Roboto", system-ui, sans-serif', letterSpacing: '0.22em' }}
              aria-label="Sports"
            >
              <Link to="/sports/football" className="min-h-11 inline-flex items-center hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#71cdd9] rounded-sm">Football</Link>
              <span className="text-[#71cdd9]" aria-hidden>·</span>
              <Link to="/sports/basketball" className="min-h-11 inline-flex items-center hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#71cdd9] rounded-sm">Basketball</Link>
              <span className="text-[#71cdd9]" aria-hidden>·</span>
              <Link to="/sports/padel" className="min-h-11 inline-flex items-center hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#71cdd9] rounded-sm">Padel</Link>
            </nav>

            <div className="relative mt-6 sm:mt-8">
              <LandingUploadGate />
            </div>
          </div>

          <div className="mt-12 w-full lg:mt-0">
            <AppPhoneTrio priority />
          </div>
        </div>

        <div
          className="hero-bottom-fade absolute bottom-0 left-0 right-0 h-16 sm:h-28 pointer-events-none z-[1]"
          style={{
            background:
              'linear-gradient(to top, #000000 0%, rgba(0,0,0,0.7) 40%, transparent 100%)',
            ...(lite
              ? {}
              : {
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                }),
          }}
        />
      </section>

      <StickyWhatsApp />
    </>
  )
}
